import { tableTagsFilter, tableTagsRender } from "@/lib/tags";
import { compareNoCase } from "@/lib/utils";
import { composeAddress, composePhone } from "@repo/shared/composeStrings";
import { promiseGuts } from "@repo/lib/promiseGuts";
import { arrayify, capitalize } from "@repo/shared/utils";
import { DataTableColumnHeader } from "@repo/ui/components/Base/DataTableColumnHeader";
import type { ColumnDef, ColumnMeta } from "@tanstack/react-table";
import { format } from "date-fns";
import { UserSummaryItem } from "../users/UserSummaryItem";
import type {
  Address,
  BaseFirestore,
  PhoneNumber,
  Team,
  TeamMemberWContact,
  User,
  UserWRole,
} from "@repo/types";
import { TeamSummaryItem } from "../teams/TeamSummaryItem";
import { DATE_FORMAT, DATETIME_FORMAT } from "@/lib/constants";
import type { UsedByFilterValue } from "@repo/ui/components/Base/DataTableFilters";
import { AddressDisplay } from "../users/AddressDisplay";
import { PhoneDisplay } from "../users/PhoneDisplay";
import { RoleDefinitionsDialog } from "../common/RoleDefinitionsDialog";
import { Chip } from "@repo/ui/components/Base/Chip";
import { firstValueFrom, type Observable } from "rxjs";
import { userRepo } from "@/repositories/userRepo";
import { teamRepo } from "@/repositories/teamRepo";

export type RepoGet<T> = (tenantId: string, id: string) => Observable<T | null>;

// Cache Maps

// A class for caching results of an async lookup
export class CacheMap<T> {
  #waiting = new Map<string, Promise<T | null>>();
  map = new Map<string, T | null>();
  getter: RepoGet<T>;

  constructor(getter: RepoGet<T>) {
    this.getter = getter;
  }

  async get(tenantId: string, id: string): Promise<T | null> {
    if (!id) return null;

    if (!this.map.has(id)) {
      // if something else already requested this lookup, use that promise instead
      const prom = this.#waiting.get(id);
      if (prom) return await prom;

      // otherwise, start a new promise, and load
      const { resolve, promise } = promiseGuts<T | null>();
      this.#waiting.set(id, promise);

      const item = await firstValueFrom(this.getter(tenantId, id));
      this.map.set(id, item);

      resolve(item);
    }
    return this.map.get(id) ?? null;
  }
}

// CacheMap of user object lookup by id
export class UserMap extends CacheMap<User> {
  constructor() {
    super((_tenantId, id) => userRepo.get(id));
  }
}

// CacheMap of team object lookup by id
export class TeamMap extends CacheMap<Team> {
  constructor() {
    super((tenantId, id) => teamRepo.get(tenantId, id));
  }
}

// CacheMap of generic object lookup by id
export class GenericMap<T extends BaseFirestore> extends CacheMap<T> {
  constructor(repo: { get: RepoGet<T> }) {
    super((tenantId, id) => repo.get(tenantId, id));
  }
}

// Common Async Filter Functions

export const getUsedByFilterFn = <
  T extends BaseFirestore,
  Other extends BaseFirestore,
>(
  key: keyof T,
  repo: { get: RepoGet<Other> },
  tenantId: string,
  nameKey: keyof Other
) =>
  (async (row, _columnId, rawFilterValue, _table, rawState: object) => {
    const filterValue = rawFilterValue as UsedByFilterValue;

    // if filter is off, always match
    if (!filterValue || (!filterValue.mode && !filterValue.name)) return true;

    // set up state
    const state = rawState as { map: GenericMap<Other> };
    state.map ??= new GenericMap<Other>(repo);

    // convert ids to objects
    const ids = arrayify<string>(row.original[key] ?? []);
    const objects = await Promise.all(
      ids.map((id) => state.map.get(tenantId, id))
    );

    // apply filter test

    switch (filterValue.mode ?? "name") {
      case "name": {
        const query = (filterValue.name ?? "").toLowerCase();
        if (!query) return true;

        return objects.some((object) =>
          (object?.[nameKey] as string).toLowerCase().includes(query)
        );
      }
      case "none": {
        return objects.length === 0;
      }
      case "some": {
        return objects.length > 0;
      }
    }
    return true; // sanity
  }) satisfies ColumnMeta<T, unknown>["asyncFilterFn"];

// Column Definitions

export const createIdColumn = <T,>(): ColumnDef<T> => ({
  accessorKey: "id",
  meta: {
    label: "ID",
  },
  header: ({ column }) => <DataTableColumnHeader column={column} title="ID" />,
});

export const createTagColumn = <T,>(label: string = "Tags"): ColumnDef<T> => ({
  accessorKey: "tags",
  enableSorting: false,
  meta: {
    label,
  },
  header: ({ column }) => (
    <DataTableColumnHeader column={column} title={label} />
  ),
  cell: tableTagsRender(),
  filterFn: tableTagsFilter,
});

export const createNestedTagColumn = <T,>(label: string): ColumnDef<T> => ({
  accessorKey: "$nestedTags",
  enableSorting: false,
  meta: {
    label,
  },
  header: ({ column }) => (
    <DataTableColumnHeader column={column} title={label} />
  ),
  cell: tableTagsRender({ tagsKey: "$nestedTags" as keyof T }),
  filterFn: tableTagsFilter,
});

export const createDescriptionColumn = <
  T extends { description?: string | null },
>(): ColumnDef<T> => ({
  accessorKey: "description",
  meta: {
    label: "Description",
  },
  cell: ({ row }) => {
    const limit = 300;
    const description = row.original.description ?? "";
    return description.length > limit
      ? description.slice(0, limit) + "..."
      : description;
  },
  header: ({ column }) => (
    <DataTableColumnHeader column={column} title="Description" />
  ),
});

export const createNameColumn = <T,>(): ColumnDef<T> => ({
  accessorKey: "name",
  meta: {
    label: "Name",
  },
  header: ({ column }) => (
    <DataTableColumnHeader column={column} title="Name" />
  ),
});

export const createCreatedAtColumn = <T,>(): ColumnDef<T> => ({
  accessorKey: "createdAt",
  filterFn: "date",
  meta: {
    label: "Created",
    filterVariant: "date",
  },
  header: ({ column }) => (
    <DataTableColumnHeader column={column} title="Created" />
  ),
  cell: ({ row }) => {
    return <div>{format(row.getValue("createdAt"), DATETIME_FORMAT)}</div>;
  },
});

export const createCreatedByColumn = <T extends { createdBy: string }>(
  tenantId: string
): ColumnDef<T> => ({
  accessorKey: "createdBy",
  filterFn: "async",
  meta: {
    label: "Created By",
    async asyncFilterFn(row, _columnId, rawFilterValue, _table, rawState) {
      const filterValue = (rawFilterValue ?? "") as string;
      if (!filterValue) return true;

      // set up state
      const state = rawState as { map: UserMap };
      state.map ??= new UserMap();

      // if not set, load name from user
      const user = await state.map.get(tenantId, row.original.createdBy);
      const userName = user
        ? `${user.firstName} ${user.lastName}`.toLowerCase()
        : "";

      // resolve with name matching query
      return userName.includes(filterValue.toLowerCase());
    },
  },
  header: ({ column }) => (
    <DataTableColumnHeader column={column} title="Created By" />
  ),
  cell: ({ row }) => {
    const item = row.original;
    return <UserSummaryItem tenantId={tenantId} userId={item.createdBy} />;
  },
});

export const createUpdatedAtColumn = <T,>(): ColumnDef<T> => ({
  accessorKey: "updatedAt",
  filterFn: "date",
  meta: {
    label: "Last Modified",
    filterVariant: "date",
  },
  header: ({ column }) => (
    <DataTableColumnHeader column={column} title="Last Modified" />
  ),
  cell: ({ row }) => {
    return <div>{format(row.getValue("updatedAt"), DATETIME_FORMAT)}</div>;
  },
});

export const createUpdatedByColumn = <T extends { updatedBy: string }>(
  tenantId: string
): ColumnDef<T> => ({
  accessorKey: "updatedBy",
  filterFn: "async",
  meta: {
    label: "Modified By",
    async asyncFilterFn(row, _columnId, rawFilterValue, _table, rawState) {
      const filterValue = (rawFilterValue ?? "") as string;
      if (!filterValue) return true;

      // set up state
      const state = rawState as { map: UserMap };
      state.map ??= new UserMap();

      // if not set, load name from user
      const user = await state.map.get(tenantId, row.original.updatedBy);
      const userName = user
        ? `${user.firstName} ${user.lastName}`.toLowerCase()
        : "";

      // resolve with name matching query
      return userName.includes(filterValue.toLowerCase());
    },
  },
  header: ({ column }) => (
    <DataTableColumnHeader column={column} title="Modified By" />
  ),
  cell: ({ row }) => {
    const item = row.original;
    return <UserSummaryItem tenantId={tenantId} userId={item.updatedBy} />;
  },
});

export const createTeamColumn = <T extends { teamId?: string }>(
  tenantId: string
): ColumnDef<T> => ({
  accessorKey: "teamId",
  filterFn: "async",
  meta: {
    label: "Team",
    async asyncFilterFn(row, _columnId, rawFilterValue, _table, rawState) {
      const filterValue = (rawFilterValue ?? "") as string;
      if (!filterValue) return true;

      // set up state
      const state = rawState as { map: TeamMap };
      state.map ??= new TeamMap();

      // if not set, load team name
      const team = await state.map.get(tenantId, row.original.teamId);
      return (team?.name?.toLowerCase() ?? "").includes(
        filterValue.toLowerCase()
      );
    },
  },
  header: ({ column }) => (
    <DataTableColumnHeader column={column} title="Team" />
  ),
  cell: ({ row }) => {
    const item = row.original;
    return <TeamSummaryItem tenantId={tenantId} teamId={item.teamId ?? ""} />;
  },
});

export const createStatusColumn = <T,>(): ColumnDef<T> => ({
  accessorKey: "status",
  meta: {
    label: "Status",
    filterVariant: "selectStatus",
  },
  sortingFn: "text",
  header: ({ column }) => (
    <DataTableColumnHeader column={column} title="Status" />
  ),
  cell: ({ row }) => {
    return <div>{capitalize(row.getValue("status"))}</div>;
  },
});

export const createExpirationDateColumn = <T,>(
  label: string = "Expiration Date"
): ColumnDef<T> => ({
  accessorKey: "expirationDate",
  filterFn: "date",
  meta: {
    label,
    filterVariant: "date",
  },
  header: ({ column }) => (
    <DataTableColumnHeader column={column} title={label} />
  ),
  cell: ({ row }) => {
    const date = row.getValue("expirationDate") as Date | undefined;
    return <div>{date ? format(date, DATE_FORMAT) : ""}</div>;
  },
});

const emptyAddress: Address = {
  type: "",
  state: "",
  street: "",
  street2: "",
  city: "",
  country: "",
  zip: "",
};

export const createAddressColumn = <T,>(
  accessorFn: (row: T) => Address | undefined = ((row: { address?: Address }) =>
    row.address) as (row: T) => Address | undefined
): ColumnDef<T> => ({
  id: "address",
  accessorFn,
  meta: {
    label: "Address",
  },
  filterFn: (row, _, value) => {
    const address = accessorFn(row.original);
    if (!address) return false;

    return Object.keys(address).some((key) =>
      address[key as keyof Address].includes(value)
    );
  },
  sortingFn: (a, b) =>
    compareNoCase(
      composeAddress(accessorFn(a.original) ?? emptyAddress),
      composeAddress(accessorFn(b.original) ?? emptyAddress)
    ),
  header: ({ column }) => (
    <DataTableColumnHeader column={column} title="Address" />
  ),
  cell: ({ row }) => <AddressDisplay address={accessorFn(row.original)} />,
});

export const createPhoneColumn = <T,>(
  accessorFn: (row: T) => PhoneNumber[] = ((row: { phone?: PhoneNumber[] }) =>
    row.phone) as (row: T) => PhoneNumber[]
): ColumnDef<T> => ({
  id: "phone",
  accessorFn,
  meta: {
    label: "Phone Number",
  },
  filterFn: (row, _, value) => {
    const phones = accessorFn(row.original);
    if (!phones) return false;

    return phones.some((phone) =>
      Object.keys(phone).some((key) =>
        phone[key as keyof PhoneNumber].includes(value)
      )
    );
  },
  sortingFn: (a, b) =>
    compareNoCase(
      accessorFn(a.original)
        .map((phone) => composePhone(phone))
        .join("\n"),
      accessorFn(b.original)
        .map((phone) => composePhone(phone))
        .join("\n")
    ),
  header: ({ column }) => (
    <DataTableColumnHeader column={column} title="Phone Number" />
  ),
  cell: ({ row }) => (
    <div>
      {accessorFn(row.original).map((phone, i) => (
        <PhoneDisplay key={i} phone={phone} />
      ))}
    </div>
  ),
});

export const createRoleColumn = <
  T extends UserWRole | TeamMemberWContact,
>(): ColumnDef<T> => ({
  accessorKey: "role",
  meta: {
    label: "Role",
    filterVariant: "customSelect",
    customSelectOptions: [
      { label: "Admin", value: "admin" },
      { label: "Edit", value: "edit" },
      { label: "Submit", value: "submit" },
      { label: "View", value: "view" },
    ],
  },
  sortingFn: "text",
  header: ({ column }) => (
    <div className="flex flex-row gap-x-1 items-center">
      <DataTableColumnHeader column={column} title="Role" noSort />
      <RoleDefinitionsDialog
        iconStyle="text-inherit w-5"
        triggerStyle="flex w-8 h-8 self-center"
      />
    </div>
  ),
  cell: ({ row }) => {
    const user = row.original;
    return (
      <div className="flex flex-row flex-wrap">
        <Chip
          variant={
            user.role === "admin"
              ? "default"
              : user.role === "edit"
                ? "secondary"
                : user.role === "submit"
                  ? "tertiary"
                  : "ghost"
          }
        >
          {capitalize(user.role)}
        </Chip>
      </div>
    );
  },
});
